/** SC-838 : saut de catégorie fiable — la section se pose SOUS les barres
 *  collantes, et y reste même quand la mise en page bouge encore.
 *
 *  Deux problèmes distincts se cumulaient.
 *
 *  1. **La marge de saut était une constante fausse.** Les sections portaient
 *     `scroll-mt-[230px] lg:scroll-mt-[100px]` alors que la hauteur réelle des
 *     barres collantes dépend du parcours (mesures preprod, 2026-09-03) :
 *     116 px sur place (en-tête 60 + barre 56), 160 px à emporter (104 + 56),
 *     118 px sur ordinateur. Le saut posait donc la section 70 à 114 px trop
 *     bas — la fin de la catégorie PRÉCÉDENTE occupait le haut de l'écran,
 *     ce qui se lit exactement comme « ça n'a pas sauté au bon endroit » —
 *     et 18 px trop haut sur ordinateur, titre caché sous l'en-tête.
 *     D'où `MENU_SCROLL_MARGIN` : les deux hauteurs sont publiées en JS par
 *     `SiteHeader` et `CategoryPills`, donc justes dans les deux parcours,
 *     aux deux tailles d'écran, et lisibles même là où le moteur ignore
 *     `scroll-margin-top`.
 *
 *  2. **La WebView décale la cible APRÈS le saut.** Le navigateur intégré de
 *     WeChat (noyau X5/XWeb, Chromium ancien) ignore `aspect-ratio` — les
 *     cadres d'image se déplient au chargement des photos et poussent la page
 *     —, ignore `scroll-margin-top`, et peut annuler un défilement doux en
 *     plein vol. D'où le filet : on laisse le saut natif faire son travail,
 *     puis on surveille la position jusqu'à ce qu'elle se stabilise et on
 *     recale la section d'un coup si elle n'est pas à sa place. Un geste de
 *     défilement de l'utilisateur arrête tout — on ne se bat jamais contre lui.
 */

const TICK_MS = 100;
/** Assez long pour couvrir le défilement doux le plus lent PUIS une vague
 *  d'images qui se chargent — une WebView ouverte à froid (lien reçu dans une
 *  conversation, cache vide) réagence encore bien après 2,5 s ; assez court
 *  pour ne pas recaler une page que l'utilisateur a laissée là. */
const DEADLINE_MS = 4000;
const TOLERANCE_PX = 8;
/** La respiration entre le bas des barres et le titre de la catégorie. */
const GAP_PX = 8;

/** Marge de saut des sections `#cat-<id>`, pour le `scroll-margin-top` CSS.
 *  Mêmes variables que le filet ci-dessous, pour que les deux chemins visent
 *  le même pixel. Les replis repètent ceux de la barre elle-même
 *  (`CategoryPills` se colle à `var(--site-header-h, 110px)`) et ne servent que
 *  le temps que les deux barres publient leur hauteur mesurée. Un moteur sans
 *  variables CSS jette la déclaration entière : les classes `scroll-mt-*` que
 *  `MenuPage` garde en dessous reprennent alors la main, comme avant. */
export const MENU_SCROLL_MARGIN =
  'calc(var(--site-header-h, 110px) + var(--category-bar-h, 56px) + 8px)';

/** Un seul filet en vol : un nouveau saut remplace le précédent. */
let stopActiveWatcher: (() => void) | null = null;

function cssVarPx(name: string): number | null {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name);
  const value = parseFloat(raw);
  return Number.isFinite(value) ? value : null;
}

/** Où le haut de la section doit se poser dans la fenêtre : juste sous les
 *  barres collantes. Les hauteurs sont MESURÉES par leurs composants et
 *  publiées en variables CSS — une lecture JS, donc valable aussi dans les
 *  moteurs qui ne connaissent pas `scroll-margin-top`. Sans elles (barres pas
 *  encore montées), on retombe sur les constantes d'avant. */
function expectedTop(): number {
  const header = cssVarPx('--site-header-h');
  if (header === null) return window.innerWidth >= 1024 ? 100 : 230;
  return header + (cssVarPx('--category-bar-h') ?? 0) + GAP_PX;
}

export function scrollToMenuSection(el: HTMLElement, behavior: ScrollBehavior): void {
  stopActiveWatcher?.();
  window.dispatchEvent(new Event('menu-section-scroll'));
  el.scrollIntoView({ behavior, block: 'start' });

  const startedAt = Date.now();
  let lastY = window.scrollY;
  let stillTicks = 0;

  const stop = () => {
    clearInterval(timer);
    window.removeEventListener('wheel', stop);
    window.removeEventListener('touchstart', stop);
    if (stopActiveWatcher === stop) stopActiveWatcher = null;
  };

  const timer = setInterval(() => {
    if (Date.now() - startedAt > DEADLINE_MS) {
      stop();
      return;
    }
    const y = window.scrollY;
    if (y !== lastY) {
      // Le défilement glisse encore, ou une image vient de pousser la page :
      // on attend l'arrêt.
      lastY = y;
      stillTicks = 0;
      return;
    }
    stillTicks += 1;
    if (stillTicks < 2) return;
    const diff = el.getBoundingClientRect().top - expectedTop();
    // À sa place : on garde l'œil jusqu'à l'échéance, une image tardive peut
    // encore la déplacer.
    if (Math.abs(diff) <= TOLERANCE_PX) return;
    window.scrollBy({ top: diff, behavior: 'instant' });
    if (window.scrollY === y) {
      // Moteur sans ScrollToOptions : la forme positionnelle passe partout.
      window.scrollBy(0, diff);
    }
    if (window.scrollY === y) {
      // Bas de page : la section ne peut pas monter plus haut.
      stop();
      return;
    }
    lastY = window.scrollY;
  }, TICK_MS);

  window.addEventListener('wheel', stop, { passive: true });
  window.addEventListener('touchstart', stop, { passive: true });
  stopActiveWatcher = stop;
}
